// Screw positions with respect to the edges of elements 1 and 2: a dimensioned drawing of each element in its own
// plane plus the edge-distance table, used by the on-screen card and by the report.
// Element 2: x horizontal (−b2/2…b2/2), y vertical (real end at y = −d2).
// Element 1: v horizontal (−b1/2…b1/2), u vertical with +u towards F (real end at u = −d1).
// Only admissible screws are dimensioned (the others are red and must not be installed); a continuous element has no
// real end, so its end distance is not given.
import type { EngineTexts } from "../../../http/screwConnector/formulas/ctx";
import type { CalcState } from "../../../http/screwConnector/formulas/state";
import { fmtN } from "./reportFormulas";

type PosPoint = { h: number; v: number; ok: boolean; k?: number };

type PositionSvgConfig = {
  width: number;
  endV: number;
  pts: PosPoint[];
  continuous: boolean;
  rScrew: number;
  showNumbers: boolean;
  extra?: PosPoint[];
};

const SVG_FONT = "'DS Sans',Verdana,sans-serif";

// distances in whole mm
export function posFmt(v: number): string {
  return fmtN(Math.round(v), 0);
}

export function buildPositionSvg(cfg: PositionSvgConfig): string {
  const { width, endV, pts, continuous, rScrew, showNumbers, extra } = cfg;
  const ink = "#111827",
    dimC = "#374151",
    badC = "#E22D20";
  const good = pts.filter((p) => p.ok),
    ref = good.length ? good : pts;
  const hMin = Math.min(...ref.map((p) => p.h)),
    hMax = Math.max(...ref.map((p) => p.h));
  const vMin = Math.min(...ref.map((p) => p.v)),
    vMax = Math.max(...ref.map((p) => p.v));
  const span = Math.max(vMax - vMin, 1),
    L = width / 2;
  const fs = Math.max(width * 0.05, 4);
  const trueGap = continuous ? 0 : Math.max(vMin - endV, 0);
  const cap = Math.max(width * 0.6, span * 0.8, fs * 6);
  // a long gap down to the element end is drawn shortened, with a break line
  const broken = trueGap > cap,
    gapDraw = broken ? cap : trueGap,
    k = trueGap > 0 ? gapDraw / trueGap : 1;
  const Y = (v: number) => (v >= vMin ? -v : -vMin + (vMin - v) * k);
  const topY = -(vMax + 0.3 * span + 0.15 * width);
  const bottomY = continuous ? -vMin + 0.5 * Math.max(span, width * 0.3) : Y(endV);
  const xDimV = L + 3.4 * fs,
    yDimH = bottomY + 3.4 * fs;
  const vx0 = -L - 2 * fs,
    vx1 = L + 8 * fs,
    vy0 = topY - 2 * fs,
    vy1 = bottomY + 7 * fs;
  const sw = fs * 0.1,
    f = (x: number) => Math.round(x * 100) / 100;
  let s = "";
  // element
  s += `<rect x="${f(-L)}" y="${f(topY)}" width="${f(width)}" height="${f(bottomY - topY)}" fill="#F3F4F6" stroke="none"/>`;
  s += `<path d="M ${f(-L)} ${f(topY)} V ${f(bottomY)}${continuous ? "" : ` H ${f(L)}`} ${continuous ? `M ${f(L)} ${f(bottomY)}` : ""} V ${f(topY)}" fill="none" stroke="${ink}" stroke-width="${f(sw * 1.6)}"/>`;
  if (broken) {
    const yb = (Y(endV) + Y(vMin)) / 2;
    const seg = (2 * L + 0.6 * fs) / 4;
    s += `<path d="M ${f(-L - fs * 0.3)} ${f(yb - fs * 0.5)} l ${f(seg)} ${f(fs * 0.7)} l ${f(seg)} ${f(-fs * 0.7)} l ${f(seg)} ${f(fs * 0.7)} l ${f(seg)} ${f(-fs * 0.7)}" fill="none" stroke="${ink}" stroke-width="${f(sw)}"/>`;
  }
  // screws
  const r = Math.max(rScrew, fs * 0.28);
  pts.forEach((p) => {
    s += `<circle cx="${f(p.h)}" cy="${f(Y(p.v))}" r="${f(r)}" fill="${p.ok ? ink : badC}"/>`;
    if (showNumbers)
      s += `<text x="${f(p.h + r * 1.4)}" y="${f(Y(p.v) - r * 1.2)}" font-size="${f(fs * 0.62)}" fill="${dimC}" font-family="${SVG_FONT}">${p.k ?? ""}</text>`;
  });
  // screws of side 3 (staggered): empty circles, not dimensioned
  (extra || []).forEach((p) => {
    s += `<circle cx="${f(p.h)}" cy="${f(Y(p.v))}" r="${f(r)}" fill="none" stroke="${p.ok ? ink : badC}" stroke-width="${f(sw * 1.2)}"/>`;
  });
  // dimensions
  const tick = (x: number, y: number) =>
    `<line x1="${f(x - fs * 0.35)}" y1="${f(y + fs * 0.35)}" x2="${f(x + fs * 0.35)}" y2="${f(y - fs * 0.35)}" stroke="${dimC}" stroke-width="${f(sw * 1.3)}"/>`;
  const txt = (x: number, y: number, str: string, rot?: boolean) =>
    `<text x="${f(x)}" y="${f(y)}" font-size="${f(fs)}" fill="${ink}" text-anchor="middle" font-family="${SVG_FONT}" font-weight="600" paint-order="stroke" stroke="#fff" stroke-width="${f(fs * 0.25)}"${rot ? ` transform="rotate(-90 ${f(x)} ${f(y)})"` : ""}>${str}</text>`;
  const ext = (x1: number, y1: number, x2: number, y2: number) =>
    `<line x1="${f(x1)}" y1="${f(y1)}" x2="${f(x2)}" y2="${f(y2)}" stroke="${dimC}" stroke-width="${f(sw * 0.8)}" stroke-dasharray="${f(fs * 0.3)} ${f(fs * 0.25)}" opacity="0.8"/>`;
  const dimH = (xa: number, xb: number, y: number, label: string | null) =>
    `<line x1="${f(xa)}" y1="${f(y)}" x2="${f(xb)}" y2="${f(y)}" stroke="${dimC}" stroke-width="${f(sw * 1.1)}"/>${tick(xa, y)}${tick(xb, y)}${label != null ? txt((xa + xb) / 2, y - fs * 0.4, label) : ""}`;
  const dimV = (ya: number, yb: number, x: number, label: string | null) =>
    `<line x1="${f(x)}" y1="${f(ya)}" x2="${f(x)}" y2="${f(yb)}" stroke="${dimC}" stroke-width="${f(sw * 1.1)}"/>${tick(x, ya)}${tick(x, yb)}${label != null ? txt(x - fs * 0.5, (ya + yb) / 2, label, true) : ""}`;
  const pHmin = ref.find((p) => p.h === hMin) ?? ref[0]!,
    pHmax = ref.find((p) => p.h === hMax) ?? ref[0]!;
  const pVmin = ref.find((p) => p.v === vMin) ?? ref[0]!,
    pVmax = ref.find((p) => p.v === vMax) ?? ref[0]!;
  // horizontal chain: left edge → first screw → last screw → right edge
  s += ext(-L, bottomY, -L, yDimH + fs * 0.6) + ext(L, bottomY, L, yDimH + fs * 0.6);
  s += ext(hMin, Y(pHmin.v) + r, hMin, yDimH + fs * 0.6);
  if (hMax - hMin > 0.05) s += ext(hMax, Y(pHmax.v) + r, hMax, yDimH + fs * 0.6);
  s += dimH(-L, hMin, yDimH, posFmt(hMin + L));
  if (hMax - hMin > 0.05) s += dimH(hMin, hMax, yDimH, posFmt(hMax - hMin));
  s += dimH(hMax, L, yDimH, posFmt(L - hMax));
  // vertical chain: end → first screw → last screw
  s += ext(pVmin.h + r, Y(vMin), xDimV + fs * 0.6, Y(vMin));
  if (vMax - vMin > 0.05) s += ext(pVmax.h + r, Y(vMax), xDimV + fs * 0.6, Y(vMax));
  if (!continuous) {
    s += ext(L, bottomY, xDimV + fs * 0.6, bottomY);
    s += dimV(bottomY, Y(vMin), xDimV, posFmt(vMin - endV));
  }
  if (vMax - vMin > 0.05) s += dimV(Y(vMin), Y(vMax), xDimV, posFmt(vMax - vMin));
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${f(vx0)} ${f(vy0)} ${f(vx1 - vx0)} ${f(vy1 - vy0)}" role="img" style="width:100%;height:auto;max-height:520px;display:block">${s}</svg>`;
}

// Figures, notes and table for both elements (on-screen card and report). blockReason: why the calculation did not
// run (default: state.blocked), the geometry may then contain NaN.
export function buildPositionHtml(state: CalcState | null, blockReason: string | null | undefined, t: EngineTexts): string {
  const p = t.positioning;
  if (!state || blockReason || state.blocked) return `<p class="pos-note">${p.notAvailable}</p>`;
  const grid = state.grid || [];
  const ok = grid.filter((point) => point.ok);
  if (!grid.length || !ok.length) return `<p class="pos-note">${p.none}</p>`;
  const nShow = ok.length;
  const showNumbers = grid.length <= 24;
  const rScrew = Number.isFinite(state.d) ? state.d / 2 : 2;
  const pts1: PosPoint[] = grid.map((point, i) => ({ h: point.v, v: point.u, ok: point.ok, k: i + 1 }));
  const pts2: PosPoint[] = grid.map((point, i) => ({ h: point.x, v: point.y, ok: point.ok, k: i + 1 }));
  const cont1 = !!(state.lines1 && state.lines1.continuo1),
    cont2 = !!(state.bounds2 && state.bounds2.continuo2);
  const isSteel = state.materiale1 === "acciaio";
  const stag = !!(state.twoSides && state.stagger && state.gridB);
  const gB = stag && state.gridB ? state.gridB : [];
  const svg1 = buildPositionSvg({
    width: state.b1,
    endV: -state.d1,
    pts: pts1,
    continuous: cont1,
    rScrew,
    showNumbers,
    extra: gB.map((point) => ({ h: point.v, v: point.u, ok: point.ok })),
  });
  const svg2 = buildPositionSvg({
    width: state.b2,
    endV: -state.d2,
    pts: pts2,
    continuous: cont2,
    rScrew,
    showNumbers,
    extra: gB.map((point) => ({ h: point.x, v: point.y, ok: point.ok })),
  });
  const plates = state.panel1 ? p.panelsWord : p.platesWord;
  const cap1 = `${
    state.steel3
      ? state.panel1
        ? p.captionElements13Panels
        : p.captionElements13Plates
      : p.captionElement1 +
        (isSteel ? p.suffixPlate : state.panel1 ? p.suffixPanel : state.three ? p.suffixCentral : "")
  }${p.captionView1}${cont1 ? p.continuousNoEnd : ""}`;
  const cap2 = `${
    state.three ? p.captionElements23 : state.steel3 ? p.captionElement2Timber : p.captionElement2
  }${p.captionView2}${cont2 ? p.continuousNoEnd : ""}`;
  let h = `<div class="pos-figs"><figure><figcaption>${cap1}</figcaption>${svg1}</figure><figure><figcaption>${cap2}</figcaption>${svg2}</figure></div>`;
  h += `<p class="pos-note">${p.note({
    s1: posFmt(state.s11),
    s2: posFmt(state.s12),
    axis2: state.three ? p.axisElements23 : p.axisElement2,
    spread: !!state.spreadActive,
    s1Min: posFmt(state.s11Min),
    s2Min: posFmt(state.s12Min),
    rows: state.rows,
    cols: state.cols,
    staggered: !!state.sfalsaViti,
    nShow,
    nTotal: grid.length,
    threeD: !!state.threeD,
    twoSides: !!(state.three && !state.threeD),
    steel3: !!state.steel3,
    panel1: !!state.panel1,
    stagger: stag,
    d: fmtN(state.d, 1),
    dir: state.staggerDir ?? "",
  })}</p>`;
  if (nShow <= 40) {
    const rows = ok
      .map((point) => {
        const k = grid.indexOf(point) + 1;
        return `<tr><td>${k}</td><td>${cont1 ? "—" : posFmt(point.u + state.d1)}</td><td>${posFmt(point.v + state.b1 / 2)}</td><td>${posFmt(state.b1 / 2 - point.v)}</td><td>${posFmt(point.x + state.b2 / 2)}</td><td>${posFmt(state.b2 / 2 - point.x)}</td><td>${cont2 ? "—" : posFmt(point.y + state.d2)}</td></tr>`;
      })
      .join("");
    h += `<table class="pos-table"><thead><tr><th rowspan="2">${p.tableNo}</th><th colspan="3">${state.steel3 ? p.tableElements13(plates) : p.tableElement1} — ${p.tableDistance}</th><th colspan="3">${state.three ? p.tableElements23 : p.tableElement2} — ${p.tableDistance}</th></tr><tr><th>${p.tableEnd}</th><th>${p.tableLeft}</th><th>${p.tableRight}</th><th>${p.tableLeft}</th><th>${p.tableRight}</th><th>${p.tableEnd}</th></tr></thead><tbody>${rows}</tbody></table>`;
  } else {
    const xs = (a: typeof ok, fn: (point: (typeof ok)[number]) => number) => Math.min(...a.map(fn));
    const xm = (a: typeof ok, fn: (point: (typeof ok)[number]) => number) => Math.max(...a.map(fn));
    const el2 = state.three ? p.tableElements23 : p.tableElement2;
    h +=
      `<table class="pos-table"><thead><tr><th>${p.tableMinMaxTitle}</th><th>${p.tableMin}</th><th>${p.tableMax}</th></tr></thead><tbody>` +
      (cont1
        ? ""
        : `<tr><td>${p.tableFromEnd1}</td><td>${posFmt(xs(ok, (point) => point.u + state.d1))}</td><td>${posFmt(xm(ok, (point) => point.u + state.d1))}</td></tr>`) +
      `<tr><td>${p.tableFromLeft1}</td><td>${posFmt(xs(ok, (point) => point.v + state.b1 / 2))}</td><td>${posFmt(xm(ok, (point) => point.v + state.b1 / 2))}</td></tr>` +
      `<tr><td>${p.tableFromLeft2(el2)}</td><td>${posFmt(xs(ok, (point) => point.x + state.b2 / 2))}</td><td>${posFmt(xm(ok, (point) => point.x + state.b2 / 2))}</td></tr>` +
      (cont2
        ? ""
        : `<tr><td>${p.tableFromBottom2(el2)}</td><td>${posFmt(xs(ok, (point) => point.y + state.d2))}</td><td>${posFmt(xm(ok, (point) => point.y + state.d2))}</td></tr>`) +
      `</tbody></table><p class="pos-note">${p.tableMoreThan40}</p>`;
  }
  return h;
}

// Content of the on-screen card (updatePositionCard).
export function positionCardHtml(state: CalcState | null, blockReason: string | null | undefined, t: EngineTexts): string {
  if (!state || blockReason || state.blocked) return `<p class="pos-note">${t.positioning.notAvailableSee}</p>`;
  // a continuous element has no length (its d1/d2 field is hidden and may be empty)
  const okDims = [
    state.b1,
    state.lines1?.continuo1 ? 0 : state.d1,
    state.b2,
    state.bounds2?.continuo2 ? 0 : state.d2,
    state.theta,
  ].every(Number.isFinite);
  return okDims ? buildPositionHtml(state, null, t) : `<p class="pos-note">${t.positioning.missingDims}</p>`;
}
